/**
 * Coordinate Normalization Utilities
 *
 * Utilities for converting between pixel coordinates and normalized coordinates (0-1).
 * Normalized coordinates ensure polygons scale correctly at any zoom level or screen size.
 *
 * @example
 * ```ts
 * import { normalizePoint, denormalizePoint } from '@/utils/coordinates';
 *
 * // Convert click position to normalized coordinates for storage
 * const normalized = normalizePoint({ x: 500, y: 250 }, { width: 1000, height: 500 });
 * // Result: { x: 0.5, y: 0.5 }
 *
 * // Convert normalized coordinates to pixels for rendering
 * const pixels = denormalizePoint({ x: 0.5, y: 0.5 }, { width: 800, height: 400 });
 * // Result: { x: 400, y: 200 }
 * ```
 */

import type { Point } from '@flash/shared';

/**
 * Dimensions of an image or canvas in pixels
 */
export interface Dimensions {
  /** Width in pixels */
  width: number;
  /** Height in pixels */
  height: number;
}

/**
 * Pixel coordinate (not normalized)
 */
export interface PixelPoint {
  /** X coordinate in pixels */
  x: number;
  /** Y coordinate in pixels */
  y: number;
}

/**
 * Validate that dimensions are positive
 *
 * @param dimensions - Dimensions to validate
 * @throws Error if dimensions are not positive
 */
const validateDimensions = (dimensions: Dimensions): void => {
  if (dimensions.width <= 0 || dimensions.height <= 0) {
    throw new Error('Dimensions must be positive');
  }
};

/**
 * Normalize a pixel coordinate to a 0-1 range
 *
 * Converts pixel coordinates to normalized coordinates where:
 * - (0, 0) is the top-left corner
 * - (1, 1) is the bottom-right corner
 *
 * Note: Values outside 0-1 range are allowed (for points outside the image bounds)
 *
 * @param point - Pixel coordinate to normalize
 * @param dimensions - Image dimensions in pixels
 * @returns Normalized point with x and y in 0-1 range (or outside if point is outside bounds)
 * @throws Error if dimensions are not positive
 *
 * @example
 * ```ts
 * const normalized = normalizePoint({ x: 500, y: 250 }, { width: 1000, height: 500 });
 * // Result: { x: 0.5, y: 0.5 }
 * ```
 */
export const normalizePoint = (point: PixelPoint, dimensions: Dimensions): Point => {
  validateDimensions(dimensions);

  return {
    x: point.x / dimensions.width,
    y: point.y / dimensions.height,
  };
};

/**
 * Denormalize a 0-1 coordinate to pixel space
 *
 * Converts normalized coordinates back to pixel coordinates for rendering.
 *
 * @param point - Normalized point (0-1 range)
 * @param dimensions - Target dimensions in pixels
 * @returns Pixel coordinate
 * @throws Error if dimensions are not positive
 *
 * @example
 * ```ts
 * const pixels = denormalizePoint({ x: 0.5, y: 0.5 }, { width: 800, height: 400 });
 * // Result: { x: 400, y: 200 }
 * ```
 */
export const denormalizePoint = (point: Point, dimensions: Dimensions): PixelPoint => {
  validateDimensions(dimensions);

  return {
    x: point.x * dimensions.width,
    y: point.y * dimensions.height,
  };
};

/**
 * Normalize an array of pixel coordinates to 0-1 range
 *
 * @param polygon - Array of pixel coordinates
 * @param dimensions - Image dimensions in pixels
 * @returns Array of normalized points
 * @throws Error if dimensions are not positive
 *
 * @example
 * ```ts
 * const normalized = normalizePolygon([
 *   { x: 0, y: 0 },
 *   { x: 1000, y: 0 },
 *   { x: 1000, y: 500 },
 * ], { width: 1000, height: 500 });
 * // Result: [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }]
 * ```
 */
export const normalizePolygon = (polygon: PixelPoint[], dimensions: Dimensions): Point[] => {
  return polygon.map((point) => normalizePoint(point, dimensions));
};

/**
 * Denormalize an array of 0-1 coordinates to pixel space
 *
 * @param polygon - Array of normalized points
 * @param dimensions - Target dimensions in pixels
 * @returns Array of pixel coordinates
 * @throws Error if dimensions are not positive
 *
 * @example
 * ```ts
 * const pixels = denormalizePolygon([
 *   { x: 0, y: 0 },
 *   { x: 1, y: 0 },
 *   { x: 1, y: 1 },
 * ], { width: 800, height: 400 });
 * // Result: [{ x: 0, y: 0 }, { x: 800, y: 0 }, { x: 800, y: 400 }]
 * ```
 */
export const denormalizePolygon = (polygon: Point[], dimensions: Dimensions): PixelPoint[] => {
  return polygon.map((point) => denormalizePoint(point, dimensions));
};

/**
 * Clamp a value to the normalized 0-1 range
 *
 * @param value - Value to clamp
 * @returns Value clamped to 0-1 range
 *
 * @example
 * ```ts
 * clampNormalized(-0.1); // 0
 * clampNormalized(0.5);  // 0.5
 * clampNormalized(1.5);  // 1
 * ```
 */
export const clampNormalized = (value: number): number => {
  return Math.max(0, Math.min(1, value));
};

/**
 * Flatten a polygon's points into a flat array for Konva Line component
 *
 * Konva's Line component expects points as a flat array [x1, y1, x2, y2, ...]
 *
 * @param polygon - Array of points
 * @returns Flat array of coordinates
 *
 * @example
 * ```ts
 * const flat = flattenPolygonPoints([
 *   { x: 0, y: 0 },
 *   { x: 100, y: 0 },
 *   { x: 100, y: 100 },
 * ]);
 * // Result: [0, 0, 100, 0, 100, 100]
 * ```
 */
export const flattenPolygonPoints = (polygon: PixelPoint[]): number[] => {
  return polygon.flatMap((point) => [point.x, point.y]);
};
